import { useCallback, useEffect, useState } from "react";
import { nanoid } from "nanoid";
import { uploadFileToGCS } from "../utils/uploader";
import axiosInstance from "../../../../../api/axiosInstance";

export function useProductDrafts(businessId) {
  const [original, setOriginal] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [removed, setRemoved] = useState([]); // ids scheduled for DELETE
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Load ---------------------------------------------------------------------
  const reload = useCallback(async () => {
    const { data } = await axiosInstance.get("/products", {
      params: { business_id: businessId },
    });
    setOriginal(data);
    setDrafts(data);
  }, [businessId]);

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } catch (e) {
        setError("Could not fetch products");
      }
    })();
  }, [reload]);

  // Helpers ------------------------------------------------------------------
  const addDraft = () =>
    setDrafts((d) => [
      { id: nanoid(), isNew: true, name: "", price: 0, photos: [], video: null },
      ...d,
    ]); // stock/sku removed

  const updateDraft = (id, patch) =>
    setDrafts((d) => d.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const deleteDraft = (id) => {
    setDrafts((d) => d.filter((p) => p.id !== id));
    const wasPersisted = original.find((p) => String(p.id) === String(id));
    if (wasPersisted) setRemoved((r) => [...r, id]);
  };

  // Diff ---------------------------------------------------------------------
  function cleanForCompare(p) {
    const { isNew, photos = [], video = null, ...rest } = p || {};
    return {
      ...rest,
      photos: photos
        .slice(0, 5)
        .map((ph) => ({ id: ph.id || null, photo_url: ph.photo_url || null })),
      video: video
        ? { id: video.id || null, video_url: video.video_url || null }
        : null,
    };
  }

  function diff() {
    const added = drafts.filter((p) => p.isNew);
    const edited = drafts.filter(
      (p) =>
        !p.isNew &&
        JSON.stringify(cleanForCompare(p)) !==
          JSON.stringify(
            cleanForCompare(original.find((o) => String(o.id) === String(p.id)))
          )
    );
    return { added, edited, removed };
  }

  // Prepare ------------------------------------------------------------------
  function prepare(product) {
    const fd = new FormData();

    const { photos = [], video = null, ...rest } = product;

    // IMPORTANT: currency belongs to store settings; do not send per-product currency
    delete rest.currency;

    // Send a client_id to help backend echo it back (nice-to-have)
    if (product.isNew) fd.append("client_id", String(product.id));

    fd.append("payload", JSON.stringify(rest));

    // photos
    photos.slice(0, 5).forEach((ph) => {
      if (ph.file instanceof File && !ph.photo_url) {
        fd.append("photos[]", ph.file); // new upload
      } else if (ph.photo_url) {
        fd.append("photo_urls[]", ph.photo_url); // keep existing order
      }
    });

    // single video
    if (video?.file instanceof File && !video.video_url) {
      fd.append("video", video.file);
    } else if (video?.video_url) {
      fd.append("video_url", video.video_url);
    }

    return fd;
  }

  // SaveAll ------------------------------------------------------------------
  const saveAll = useCallback(
    async (shipping) => {
       const { added, edited, removed } = diff();
       if (!added.length && !edited.length && !removed.length && !shipping?.hasChanges) {
         return { ok: true, idMap: new Map() };
       }
  
       setBusy(true);
      setError(null);

      // helper: turn a draft product into { product, media, fulfillment }
      async function toSaveItem(p) {
        const product = {
          name: p.name,
          description: p.description,
          type: p.type,
          category_path: Array.isArray(p.category) ? p.category : [],
          price: Number(p.price ?? 0),
          weight_kg: p.weight ?? null,
          width_cm:  p.width ?? null,
          height_cm: p.height ?? null,
          depth_cm:  p.depth ?? null,
        };

        // MEDIA: only upload when a File is present; otherwise keep the URL (server will normalize to key)
        const photos = [];
        for (const ph of (p.photos || []).slice(0, 5)) {
          if (ph?.file instanceof File) {
            const key = await uploadFileToGCS(ph.file, "photo",businessId);
            photos.push(key);
          } else if (ph?.photo_url) {
            photos.push(ph.photo_url);
          }
        }
        let video = null;
        if (p.video?.file instanceof File) {
          video = await uploadFileToGCS(p.video.file, "video", businessId);
        } else if (p.video?.video_url) {
          video = p.video.video_url;
        }
        const media = { photos, video };

        // FULFILLMENT flags come from shipping.flags (single source of truth)
        const f = (shipping?.flags || {})[String(p.id)] || {};
        const fulfillment = {
          delivery: !!f.delivery,
          delivery_requires_approval: !!f.delivery_requires_approval,
          takeaway: !!f.takeaway,
          takeaway_requires_approval: !!f.takeaway_requires_approval,
        };
        return { product, media, fulfillment };
      }

      try {
        // map added/edited
        const addedPayload = [];
        for (const p of added) {
          const item = await toSaveItem(p);
          addedPayload.push({
            client_id: String(p.id), // used by backend to map to server id
            ...item,
          });
        }
        const editedPayload = [];
        for (const p of edited) {
          const item = await toSaveItem(p);
          editedPayload.push({
            id: String(p.id),
            ...item,
          });
        }

        // shipping payload: send raw assignments/flags (can include draft ids).
        // The backend maps client ids → server ids using idMap it builds while inserting.
        const shippingPayload = {
          assignments: shipping?.assignments || {},
          flags: shipping?.flags || {},
        };

        const { data: json } = await axiosInstance.post(
          "/products/save-all",
          {
            added: addedPayload,
            edited: editedPayload,
            removed: removed.map(String),
            shipping: shippingPayload,
          },
          { params: { business_id: businessId } }
        ); // json: { ok, idMap }
        // Refresh from server for signed view URLs and canonical data
        const { data: fresh } = await axiosInstance.get("/products", {
          params: { business_id: businessId },
        });
        setRemoved([]);
        setOriginal(fresh);
        setDrafts(fresh);

        return { ok: !!json?.ok, idMap: new Map(Object.entries(json?.idMap || {})) };
      } catch (e) {
        setError(e.message || "Could not save changes");
        return { ok: false, idMap: new Map() };
      } finally {
        setBusy(false);
      }
    }, [diff, businessId]
  );

  return { drafts, addDraft, updateDraft, deleteDraft, diff, saveAll, busy, error, reload };
}
